import React, { useEffect, useState } from "react";
import Layout from "../Layout";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { message, Spin } from "antd";

const ProfilePage = () => {
  const server_url = "https://api.edumonics.in";
  const accessToken = localStorage.getItem("accessToken");

  // Form state for AdminUserUpdate
  const [formData, setFormData] = useState({
    // Basic Fields
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "admin", // We'll force admin role here for clarity

    // Admin-Specific Fields
    middle_name: "",
    date_of_birth: "",
    contact_info_email: "",
    contact_info_phone: "",
    gender: "",
    address: "",

    // Professional Information
    current_school: "",
    total_experience: "",
    past_schools: "",
    educational_qualifications: "",
    professional_achievements: "",
    languages_spoken: "",
    leadership_style: "",
    committees_boards: "",

    // Visionary and Strategic Elements
    school_goals: "",
    improvement_strategies: "",
    extracurricular_strategies: "",
    teacher_development: "",

    // Skills & Expertise
    specializations: "",
    conflict_resolution: "",
    project_management: "",

    // Engagement & Feedback
    feedback: "",
    communication_channels: "",
    engaged_activities: "",

    // Administrative Details
    key_responsibilities: "",
    budget_management: "",
    disciplinary_philosophy: "",
    tech_integration: "",

    // Multimedia & Additional Fields
    video_intro: "",
    portfolio_link: "",
    personal_motto: "",

    // Personalization
    favorite_quotes: "",
    hobbies: "",
    recommended_books: "",
  });

  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);
  const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);

  // Toggle password visibility
  const togglePasswordVisibility = () => setPasswordShown(!passwordShown);
  const toggleConfirmPasswordVisibility = () =>
    setConfirmPasswordShown(!confirmPasswordShown);

  // Handle input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  // Optional: Fetch existing admin profile data from /admin/profile to prefill the form
  const fetchAdminProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${server_url}/admin/profile`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Failed to load admin profile.");
      }

      // Prefill formData with existing admin profile fields
      setFormData((prev) => ({
        ...prev,
        name: data.name || "",
        email: data.email || "",
        // We'll keep password fields empty
        role: data.role || "admin",

        // Admin-Specific
        middle_name: data.middle_name || "",
        date_of_birth: data.date_of_birth || "",
        contact_info_email: data.contact_info?.email || "",
        contact_info_phone: data.contact_info?.phone || "",
        gender: data.gender || "",
        address: data.address || "",

        // Professional Information
        current_school: data.current_school || "",
        total_experience: data.total_experience?.toString() || "",
        past_schools: Array.isArray(data.past_schools)
          ? data.past_schools.join(", ")
          : "",
        educational_qualifications: Array.isArray(data.educational_qualifications)
          ? data.educational_qualifications.join(", ")
          : "",
        professional_achievements: Array.isArray(data.professional_achievements)
          ? data.professional_achievements.join(", ")
          : "",
        languages_spoken: Array.isArray(data.languages_spoken)
          ? data.languages_spoken.join(", ")
          : "",
        leadership_style: data.leadership_style || "",
        committees_boards: Array.isArray(data.committees_boards)
          ? data.committees_boards.join(", ")
          : "",

        // Visionary & Strategic
        school_goals: data.school_goals || "",
        improvement_strategies: data.improvement_strategies || "",
        extracurricular_strategies: data.extracurricular_strategies || "",
        teacher_development: data.teacher_development || "",

        // Skills & Expertise
        specializations: Array.isArray(data.specializations)
          ? data.specializations.join(", ")
          : "",
        conflict_resolution: data.conflict_resolution || "",
        project_management: data.project_management || "",

        // Engagement & Feedback
        feedback: data.feedback || "",
        communication_channels: data.communication_channels || "",
        engaged_activities: data.engaged_activities || "",

        // Administrative
        key_responsibilities: data.key_responsibilities || "",
        budget_management: data.budget_management || "",
        disciplinary_philosophy: data.disciplinary_philosophy || "",
        tech_integration: data.tech_integration || "",

        // Multimedia & Additional
        video_intro: data.video_intro || "",
        portfolio_link: data.portfolio_link || "",
        personal_motto: data.personal_motto || "",

        // Personalization
        favorite_quotes: data.favorite_quotes || "",
        hobbies: Array.isArray(data.hobbies) ? data.hobbies.join(", ") : "",
        recommended_books: Array.isArray(data.recommended_books)
          ? data.recommended_books.join(", ")
          : "",
      }));
    } catch (error) {
      // message.error(error.message);
      console.error("Error fetching admin profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminProfile(); // Load existing admin profile data on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Submit form: calls /admin/update with AdminUserUpdate fields
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Check password match if password is provided
    if (formData.password || formData.confirmPassword) {
      if (formData.password !== formData.confirmPassword) {
        setWarning("Passwords do not match!");
        return;
      }
    }
    setWarning("");

    // Convert comma-separated strings into arrays
    const parseArrayField = (str) =>
      str
        ? str
            .split(",")
            .map((item) => item.trim())
            .filter((val) => val !== "")
        : [];

    // Build request body
    const updatePayload = {
      // Basic
      name: formData.name || undefined,
      email: formData.email || undefined,
      password: formData.password || undefined,
      role: formData.role || undefined,

      // Admin-Specific
      middle_name: formData.middle_name || undefined,
      date_of_birth: formData.date_of_birth || undefined,
      contact_info: {
        email: formData.contact_info_email || "",
        phone: formData.contact_info_phone || "",
      },
      gender: formData.gender || undefined,
      address: formData.address || undefined,

      // Professional
      current_school: formData.current_school || undefined,
      total_experience:
        formData.total_experience !== ""
          ? parseInt(formData.total_experience, 10)
          : undefined,
      past_schools: parseArrayField(formData.past_schools),
      educational_qualifications: parseArrayField(
        formData.educational_qualifications
      ),
      professional_achievements: parseArrayField(
        formData.professional_achievements
      ),
      languages_spoken: parseArrayField(formData.languages_spoken),
      leadership_style: formData.leadership_style || undefined,
      committees_boards: parseArrayField(formData.committees_boards),

      // Visionary & Strategic
      school_goals: formData.school_goals || undefined,
      improvement_strategies: formData.improvement_strategies || undefined,
      extracurricular_strategies: formData.extracurricular_strategies || undefined,
      teacher_development: formData.teacher_development || undefined,

      // Skills & Expertise
      specializations: parseArrayField(formData.specializations),
      conflict_resolution: formData.conflict_resolution || undefined,
      project_management: formData.project_management || undefined,

      // Engagement & Feedback
      feedback: formData.feedback || undefined,
      communication_channels: formData.communication_channels || undefined,
      engaged_activities: formData.engaged_activities || undefined,

      // Administrative Details
      key_responsibilities: formData.key_responsibilities || undefined,
      budget_management: formData.budget_management || undefined,
      disciplinary_philosophy: formData.disciplinary_philosophy || undefined,
      tech_integration: formData.tech_integration || undefined,

      // Multimedia & Additional Fields
      video_intro: formData.video_intro || undefined,
      portfolio_link: formData.portfolio_link || undefined,
      personal_motto: formData.personal_motto || undefined,

      // Personalization
      favorite_quotes: formData.favorite_quotes || undefined,
      hobbies: parseArrayField(formData.hobbies),
      recommended_books: parseArrayField(formData.recommended_books),
    };

    try {
      const response = await fetch(`${server_url}/admin/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updatePayload),
      });
      const data = await response.json();

      if (response.ok) {
        message.success(data.message || "Profile updated successfully!");
        // Optionally reset password fields
        setFormData((prev) => ({
          ...prev,
          password: "",
          confirmPassword: "",
        }));
      } else {
        console.error(data.detail || "Failed to update profile.");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      // message.error("Error updating profile.");
    }
  };

  // Rendering
  return (
    <Layout pageTitle="Admin Profile">
      <div className="flex flex-col items-center w-full px-4 sm:px-6 lg:px-8 mt-10">
        <div className="w-full max-w-4xl p-10 bg-white dark:bg-gray-900 rounded-lg shadow-md">
          <h2 className="text-3xl font-semibold font-gotham text-gray-800 dark:text-white mb-6 text-center">
            Update Admin Profile
          </h2>

          {loading ? (
            <div className="flex justify-center items-center">
              <Spin size="large" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 font-gotham">
              {/* BASIC INFO */}
              <div className="flex flex-col font-gotham">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2 font-gotham">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 font-gotham"
                />
              </div>

              {/* PASSWORD FIELDS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={passwordShown ? "text" : "password"}
                      name="password"
                      placeholder="Enter new password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                    <span
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-500"
                    >
                      {passwordShown ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={confirmPasswordShown ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm new password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                    <span
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-500"
                    >
                      {confirmPasswordShown ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                </div>
              </div>
              {warning && <p className="text-sm text-red-500 mt-2">{warning}</p>}

              {/* ADMIN-SPECIFIC FIELDS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Middle Name
                  </label>
                  <input
                    type="text"
                    name="middle_name"
                    value={formData.middle_name}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                    placeholder="Middle Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    name="contact_info_email"
                    value={formData.contact_info_email}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                    placeholder="Separate contact email if different"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">Phone</label>
                  <input
                    type="text"
                    name="contact_info_phone"
                    value={formData.contact_info_phone}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                    placeholder="e.g., +1 234 567 890"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other / Prefer not to say</option>
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                    placeholder="Admin's address"
                  />
                </div>
              </div>

              {/* PROFESSIONAL INFO */}
              <h3 className="text-lg font-semibold mt-8">Professional Info</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Current School
                  </label>
                  <input
                    type="text"
                    name="current_school"
                    value={formData.current_school}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Total Experience (years)
                  </label>
                  <input
                    type="number"
                    name="total_experience"
                    value={formData.total_experience}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                    min={0}
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Past Schools (comma separated)
                </label>
                <input
                  type="text"
                  name="past_schools"
                  value={formData.past_schools}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                  placeholder="e.g., School A, School B"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Educational Qualifications (comma separated)
                </label>
                <input
                  type="text"
                  name="educational_qualifications"
                  value={formData.educational_qualifications}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                  placeholder="e.g., B.Ed, M.Ed"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Professional Achievements (comma separated)
                </label>
                <input
                  type="text"
                  name="professional_achievements"
                  value={formData.professional_achievements}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Languages Spoken (comma separated)
                </label>
                <input
                  type="text"
                  name="languages_spoken"
                  value={formData.languages_spoken}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                  placeholder="e.g., English, Hindi"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Leadership Style
                </label>
                <input
                  type="text"
                  name="leadership_style"
                  value={formData.leadership_style}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg font-gotham"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Committees/Boards (comma separated)
                </label>
                <input
                  type="text"
                  name="committees_boards"
                  value={formData.committees_boards}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                />
              </div>

              {/* VISIONARY */}
              <h3 className="text-lg font-semibold mt-8">Visionary & Strategy</h3>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  School Goals
                </label>
                <textarea
                  name="school_goals"
                  value={formData.school_goals}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                  rows={3}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Improvement Strategies
                </label>
                <textarea
                  name="improvement_strategies"
                  value={formData.improvement_strategies}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                  rows={3}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Extracurricular Strategies
                </label>
                <textarea
                  name="extracurricular_strategies"
                  value={formData.extracurricular_strategies}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                  rows={3}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Teacher Development
                </label>
                <textarea
                  name="teacher_development"
                  value={formData.teacher_development}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                  rows={3}
                />
              </div>

              {/* SKILLS */}
              <h3 className="text-lg font-semibold mt-8">Skills & Expertise</h3>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Specializations (comma separated)
                </label>
                <input
                  type="text"
                  name="specializations"
                  value={formData.specializations}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Conflict Resolution
                  </label>
                  <input
                    type="text"
                    name="conflict_resolution"
                    value={formData.conflict_resolution}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Project Management
                  </label>
                  <input
                    type="text"
                    name="project_management"
                    value={formData.project_management}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
              </div>

              {/* ENGAGEMENT & FEEDBACK */}
              <h3 className="text-lg font-semibold mt-8">
                Engagement & Feedback
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">Feedback</label>
                  <input
                    type="text"
                    name="feedback"
                    value={formData.feedback}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Communication Channels
                  </label>
                  <input
                    type="text"
                    name="communication_channels"
                    value={formData.communication_channels}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Engaged Activities
                </label>
                <input
                  type="text"
                  name="engaged_activities"
                  value={formData.engaged_activities}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                />
              </div>

              {/* ADMIN DETAILS */}
              <h3 className="text-lg font-semibold mt-8">
                Administrative Details
              </h3>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Key Responsibilities
                </label>
                <textarea
                  name="key_responsibilities"
                  value={formData.key_responsibilities}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Budget Management
                  </label>
                  <input
                    type="text"
                    name="budget_management"
                    value={formData.budget_management}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Disciplinary Philosophy
                  </label>
                  <input
                    type="text"
                    name="disciplinary_philosophy"
                    value={formData.disciplinary_philosophy}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Tech Integration
                </label>
                <input
                  type="text"
                  name="tech_integration"
                  value={formData.tech_integration}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                />
              </div>

              {/* MULTIMEDIA */}
              <h3 className="text-lg font-semibold mt-8">
                Multimedia & Additional
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Video Intro
                  </label>
                  <input
                    type="text"
                    name="video_intro"
                    value={formData.video_intro}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                    placeholder="e.g., YouTube link"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Portfolio Link
                  </label>
                  <input
                    type="text"
                    name="portfolio_link"
                    value={formData.portfolio_link}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                    placeholder="e.g., personal website"
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Personal Motto
                </label>
                <input
                  type="text"
                  name="personal_motto"
                  value={formData.personal_motto}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                />
              </div>

              {/* PERSONALIZATION */}
              <h3 className="text-lg font-semibold mt-8">Personalization</h3>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2">
                  Favorite Quotes
                </label>
                <textarea
                  name="favorite_quotes"
                  value={formData.favorite_quotes}
                  onChange={handleInputChange}
                  className="p-3 border rounded-lg"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Hobbies (comma separated)
                  </label>
                  <input
                    type="text"
                    name="hobbies"
                    value={formData.hobbies}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-2">
                    Recommended Books (comma separated)
                  </label>
                  <input
                    type="text"
                    name="recommended_books"
                    value={formData.recommended_books}
                    onChange={handleInputChange}
                    className="p-3 border rounded-lg"
                  />
                </div>
              </div>

              {/* SUBMIT BUTTON */}
              <button
                type="submit"
                className="w-full font-gotham py-3 mt-4 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg shadow-md transition duration-300"
              >
                Update Admin Profile
              </button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
